/**
 * The three-pane shell (`110-design-system.md` §5). Sidebar | main | context panel.
 *
 * Criterion #8: "Each of the three panes collapses and expands; the state survives a
 * reload." Main is deliberately not collapsible — §5's table says so, and a content
 * pane you can collapse to nothing is a way to lose your editor, not a feature.
 * Widths and collapse state are persisted per pane in localStorage (`stores/layout.ts`).
 *
 * Each pane gets its own error boundary (`111` §8.1): a crash in the context panel
 * must not take the editor down with it.
 */
import { Suspense, lazy, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router';
import {
  ResizableGroup,
  ResizablePanel,
  ResizableSeparator,
  usePanelRef,
  type PanelImperativeHandle,
  type PanelSize,
} from '@/components/ui/resizable';
import { ErrorBoundary } from '@/components/shell/ErrorBoundary';
import { RightPanel } from '@/components/shell/RightPanel';
import { Sidebar } from '@/components/shell/Sidebar';
import { TopBar } from '@/components/shell/TopBar';
/**
 * The two calendar dialogs are LAZY for the same reason the calendar routes are
 * (`120` §2): they are mounted on every route because five different places open them
 * (`080` §9), but they render NOTHING until one of them does — and they drag
 * `react-day-picker` and the whole event form in behind them. `Suspense` with a `null`
 * fallback is exact here: a host that renders nothing, while loading, still renders nothing.
 */
const DueDateDialogHost = lazy(async () => ({
  default: (await import('@/features/calendar/DueDate')).DueDateDialogHost,
}));
const EventDialogHost = lazy(async () => ({
  default: (await import('@/features/calendar/EventDialog')).EventDialogHost,
}));
import { CommandPalette } from '@/features/search/CommandPalette';
import { GlobalHotkeys } from '@/keyboard/GlobalHotkeys';
import { SettingsBridge } from '@/features/settings/SettingsBridge';
import { useHotkeys, type Hotkey } from '@/hooks/useHotkeys';
import { PANE_LIMITS, usePane } from '@/stores/layout';
import { useDensity } from '@/stores/density';

/**
 * Expanding a collapsed pane back to the width the user left it at.
 *
 * `expand()` alone is not enough: the library expands to `expandToSize` — the width the
 * panel had when `collapse()` was called — and falls back to `minSize` when it has none,
 * which is the case for a pane that was collapsed before the group had measured itself.
 * The context panel is closed by default, so it hit that path every time and opened at
 * 280 instead of the 360 that §5 specifies. `expand()` then `resize()` to the width we
 * persisted ourselves is exact, and does not depend on the library's memory of it.
 */
function expandOrCollapse(panel: PanelImperativeHandle | null, size: number): void {
  if (!panel) return;

  if (panel.isCollapsed()) {
    panel.expand();
    panel.resize(size);
  } else {
    panel.collapse();
  }
}

export function AppShell() {
  const sidebarRef = usePanelRef();
  const rightRef = usePanelRef();

  // Stamps `data-density` on <html> so every control's height comes from one attribute
  // (`110` §4). The palette's "Toggle density" command flips it.
  useDensity();

  const [sidebar, setSidebar] = usePane('sidebar');
  const [right, setRight] = usePane('right');

  // Every pane MOUNTS at its persisted width; this collapses the ones that were left
  // collapsed. useLayoutEffect, not useEffect: it runs before paint, so a pane the user
  // closed does not flash open on every boot. It runs once — re-collapsing the pane the
  // user just expanded would be a fight the user loses.
  const restored = useRef(false);

  useLayoutEffect(() => {
    if (restored.current) return;
    restored.current = true;
    if (sidebar.collapsed) sidebarRef.current?.collapse();
    if (right.collapsed) rightRef.current?.collapse();
  }, [sidebar.collapsed, right.collapsed, sidebarRef, rightRef]);

  const toggleSidebar = useCallback(() => {
    expandOrCollapse(sidebarRef.current, sidebar.size);
  }, [sidebarRef, sidebar.size]);

  const toggleRight = useCallback(() => {
    expandOrCollapse(rightRef.current, right.size);
  }, [rightRef, right.size]);

  // The library owns the DOM size; we only record what must survive a reload.
  // A collapsed panel reports 0px — do not persist that as the width, or expanding
  // next time would restore a 0-wide pane.
  const onSidebarResize = useCallback(
    (size: PanelSize) => {
      const collapsed = size.inPixels < 1;
      setSidebar(collapsed ? { collapsed } : { collapsed, size: Math.round(size.inPixels) });
    },
    [setSidebar],
  );

  const onRightResize = useCallback(
    (size: PanelSize) => {
      const collapsed = size.inPixels < 1;
      setRight(collapsed ? { collapsed } : { collapsed, size: Math.round(size.inPixels) });
    },
    [setRight],
  );

  /**
   * `Mod+E` — the AI panel (`110` §7, `090` §10). Opens the right pane if it was closed and
   * closes it if it was open — the right pane holds nothing but the AI chat (`RightPanel`), so
   * "toggle the AI panel" and "toggle the pane" are now the same gesture.
   *
   * This is also why Tiptap's default `Mod+E` (inline code) is explicitly removed in the
   * editor's extensions: this binding is used a hundred times a day and inline code is not.
   */
  const toggleAi = useCallback(() => {
    expandOrCollapse(rightRef.current, right.size);
  }, [rightRef, right.size]);

  const closeRight = useCallback(() => {
    rightRef.current?.collapse();
  }, [rightRef]);

  // Memoized: `useHotkeys` registers the array BY IDENTITY into one shared dispatcher, so a
  // fresh literal on every render would churn the whole keymap's registration order.
  const hotkeys: Hotkey[] = useMemo(
    () => [
      { key: '\\', mod: true, handler: toggleSidebar }, // 110 §7
      { key: '.', mod: true, handler: toggleRight },
      { key: 'e', mod: true, handler: toggleAi },
    ],
    [toggleAi, toggleRight, toggleSidebar],
  );
  useHotkeys(hotkeys);

  return (
    <div className="flex h-full flex-col">
      {/* The keymap that belongs to no feature — the leader sequences, dark mode, and the `?`
          cheatsheet that lists every binding in the app (`110` §7). */}
      <GlobalHotkeys />

      {/* Reconciles the two preferences that live in localStorage AND on the server, and seeds
          the timezone on first boot (`100` §1, `110` §2.1). Renders nothing. */}
      <SettingsBridge />

      {/* `Mod+K` from ANYWHERE — including inside the Tiptap editor, which must not swallow
          it (`070` §4). Mounted at the shell so no route has to remember to include it. */}
      <CommandPalette />

      {/* Mounted once, driven by a tiny external store: the event dialog is opened from the
          palette, the agenda, a month cell, an hour slot and an event chip — five places, in
          three routes, none of which owns it (`080` §9). Same for the due-date dialog. */}
      <Suspense fallback={null}>
        <EventDialogHost />
        <DueDateDialogHost />
      </Suspense>

      <TopBar
        sidebarCollapsed={sidebar.collapsed}
        rightCollapsed={right.collapsed}
        onToggleSidebar={toggleSidebar}
        onToggleRight={toggleRight}
      />

      <ResizableGroup orientation="horizontal" className="min-h-0 flex-1">
        <ResizablePanel
          id="sidebar"
          panelRef={sidebarRef}
          collapsible
          collapsedSize={0}
          defaultSize={sidebar.size}
          minSize={PANE_LIMITS.sidebar.min}
          maxSize={PANE_LIMITS.sidebar.max}
          onResize={onSidebarResize}
          className="border-r border-[var(--border)]"
        >
          <ErrorBoundary level="pane" label="sidebar">
            <Sidebar />
          </ErrorBoundary>
        </ResizablePanel>

        <ResizableSeparator id="sidebar-separator" />

        <ResizablePanel id="main" minSize={PANE_LIMITS.main.min}>
          <main className="h-full overflow-y-auto">
            <ErrorBoundary level="pane" label="main">
              <Outlet />
            </ErrorBoundary>
          </main>
        </ResizablePanel>

        <ResizableSeparator id="right-separator" />

        <ResizablePanel
          id="right"
          panelRef={rightRef}
          collapsible
          collapsedSize={0}
          defaultSize={right.size}
          minSize={PANE_LIMITS.right.min}
          maxSize={PANE_LIMITS.right.max}
          onResize={onRightResize}
          className="border-l border-[var(--border)]"
        >
          <ErrorBoundary level="pane" label="right">
            {/* `Esc` in the composer, with nothing streaming, closes the panel (`110` §7). */}
            <RightPanel onClose={closeRight} />
          </ErrorBoundary>
        </ResizablePanel>
      </ResizableGroup>
    </div>
  );
}
